import React, { useMemo, useState } from 'react'
import { SectionTile } from '@reapitform/dashboard-ui'
import { Button } from '@reapit/elements/core/button'
import { toast } from '@reapit/elements/core/toaster'
import {
  AdminFormField,
  AdminReviewCard,
  AdminReviewCardSection,
  ConditionSource,
  FullFormField,
  FullReviewCard,
  ReviewEditFieldModel,
} from '../../../../api/types'
import { getEnumLabel, useAdminEnums } from '../../../../api/enums'
import { useAdminFormPreview } from '../../../../api/form-preview'
import { useAccessMode } from '../../../../access-mode-provider'
import { ReviewCardDrawer } from '../review-card-drawer'
import { SectionDrawer } from '../section-drawer'
import { NOTICE_SECTION_TYPES } from '../notice-section-types'
import { FieldDrawer } from '../field-drawer'
import { FieldPreview } from '../field-preview'
import { FactorySectionPreview } from '../factory-field-preview'
import { CollectionDrawer } from '../collection-drawer'
import { CollectionPreview } from '../collection-preview'
import { ConfirmDeleteDialog } from '../../../../components/confirm-delete-dialog'
import { useBuilderDeletes } from '../../../../api/builder-deletes'
import {
  BuilderSplitLayout,
  Canvas,
  CardTile,
  EmptyState,
  FieldPreviewGrid,
  LeftColumn,
  OutlineActions,
  OutlineAddCardLink,
  OutlineAddLink,
  OutlineCard,
  OutlineCardName,
  OutlineSection,
  OutlineSectionName,
  OutlineSectionNote,
  OutlineTree,
  SectionEditRow,
  SideCardHelp,
} from '../../style'
import {
  CanvasCardDescription,
  CardActions,
  CardDivider,
  NoSectionsNote,
  OutlineCollection,
  SectionBlock,
  SectionBlockDescription,
  SectionBlockHeader,
  SectionBlockTitle,
  FactorySectionCaption,
  SectionFamilyMismatchNote,
  SectionFooter,
  SectionsList,
  SectionTypeNote,
} from './style'

interface AgentViewProps {
  reviewCards: FullReviewCard[]
  formReferenceID: string
  versionReferenceID: string
  /** The form's document family ("Agreement" | "Notice") — narrows the Section Types offered when adding
   * a section, and flags any already-saved section whose type doesn't apply to a Notice. */
  family?: string
  onChanged: () => void
}

const isTypedSection = (section: AdminReviewCardSection): boolean =>
  Boolean(section.sectionType) && section.sectionType !== 'None'

const OPTION_EDIT_TYPES = ['SelectOption', 'RadioButton', 'Checkbox']
const COMPOSITE_EDIT_TYPES = ['Select', 'RadioGroup', 'CheckboxGroup']

/** A top-level authored entry in a None section: a field/composite, or a Collection (repeatable group). */
interface AuthoredField {
  field: AdminFormField
  /** Composite options (Select/RadioGroup); empty otherwise. */
  options: AdminFormField[]
  /** Present when this entry is a Collection: its Group ref (to add children to) and its child fields. */
  collection: { groupReferenceID: string | null; children: AdminFormField[] } | null
  /** This entry's visibility conditions — shown when ANY match (empty = always shown). Each carries the
   *  dependency row's reference (for updating/removing that specific condition) and the option it targets. */
  conditions: { dependencyRef: string; optionRef: string; controllingLabel: string; optionLabel: string }[]
}

interface SectionAnalysis {
  authored: AuthoredField[]
  sources: ConditionSource[]
}

const fieldLabel = (field?: AdminFormField): string =>
  field?.editDisplayName || field?.jsonPropertyName || field?.defaultData || 'field'

/**
 * Analyses a None section's flat field list into (a) top-level authored entries to render and (b) the
 * condition sources other fields can be shown for. Options (SelectOption/RadioButton) render under their
 * composite parent; Group fields + collection children render inside their Collection; conditional fields
 * carry a visibility condition (a dependency onto an option).
 */
const analyseSection = (entryFields: FullFormField[]): SectionAnalysis => {
  const fieldByRef = new Map(entryFields.map((entry) => [entry.field.referenceID, entry.field]))
  const entryByRef = new Map(entryFields.map((entry) => [entry.field.referenceID, entry]))
  const isOption = (field: AdminFormField) => OPTION_EDIT_TYPES.includes(field.editType)
  const isGroup = (field: AdminFormField) => field.editType === 'Group'
  const isCollection = (field: AdminFormField) => field.editType === 'Collection'
  const dependantsOf = (parentReferenceID: string): FullFormField[] =>
    entryFields.filter((entry) =>
      entry.dependencies.some((dependency) => dependency.dependencyFieldReferenceID === parentReferenceID),
    )
  const dependsOnGroup = (entry: FullFormField) =>
    entry.dependencies.some((dependency) => {
      const target = fieldByRef.get(dependency.dependencyFieldReferenceID)
      return target && isGroup(target)
    })

  const optionsFor = (parentReferenceID: string): AdminFormField[] =>
    dependantsOf(parentReferenceID)
      .filter((entry) => isOption(entry.field))
      .map((entry) => entry.field)
      .sort((a, b) => a.editOrder - b.editOrder)

  const authored: AuthoredField[] = entryFields
    // Top level excludes options (render under composites), Group fields, and collection children.
    .filter((entry) => !isOption(entry.field) && !isGroup(entry.field) && !dependsOnGroup(entry))
    .map((entry) => {
      const options = COMPOSITE_EDIT_TYPES.includes(entry.field.editType) ? optionsFor(entry.field.referenceID) : []

      let collection: AuthoredField['collection'] = null
      if (isCollection(entry.field)) {
        const groupEntry = dependantsOf(entry.field.referenceID).find((candidate) => isGroup(candidate.field))
        const groupRef = groupEntry?.field.referenceID ?? null
        const children = groupRef
          ? dependantsOf(groupRef)
              .map((child) => child.field)
              .sort((a, b) => a.editOrder - b.editOrder)
          : []
        collection = { groupReferenceID: groupRef, children }
      }

      // Visibility conditions = every dependency whose target is an option field (a field can have several —
      // e.g. shown when EITHER "Fixed" OR "Periodic" is selected on a RadioGroup).
      const conditionDeps = entry.dependencies.filter((dependency) => {
        const target = fieldByRef.get(dependency.dependencyFieldReferenceID)
        return target && isOption(target)
      })

      const conditions: AuthoredField['conditions'] = conditionDeps.map((conditionDep) => {
        const option = fieldByRef.get(conditionDep.dependencyFieldReferenceID)
        const controllingRef = entryByRef.get(conditionDep.dependencyFieldReferenceID)?.dependencies[0]
          ?.dependencyFieldReferenceID
        return {
          dependencyRef: conditionDep.referenceID,
          optionRef: conditionDep.dependencyFieldReferenceID,
          controllingLabel: fieldLabel(controllingRef ? fieldByRef.get(controllingRef) : undefined),
          optionLabel: option?.editDisplayName || option?.defaultData || 'option',
        }
      })

      return {
        field: entry.field,
        options,
        collection,
        conditions,
      }
    })
    .sort((a, b) => a.field.editOrder - b.field.editOrder)

  const sources: ConditionSource[] = entryFields
    .filter((entry) => COMPOSITE_EDIT_TYPES.includes(entry.field.editType))
    .map((entry) => ({
      fieldReferenceID: entry.field.referenceID,
      label: fieldLabel(entry.field),
      options: optionsFor(entry.field.referenceID).map((option) => ({
        referenceID: option.referenceID,
        label: option.editDisplayName || option.defaultData || 'option',
      })),
    }))

  return { authored, sources }
}

/**
 * The Agent View builder tab. Cards → sections → fields. Typed sections are factory-driven; `None` sections
 * hold manually-authored scalar fields (add/edit via the field drawer). Everything refreshes on save.
 */
export const AgentView: React.FC<AgentViewProps> = ({
  reviewCards,
  formReferenceID,
  versionReferenceID,
  family,
  onChanged,
}) => {
  const { data: enums } = useAdminEnums()
  const { data: preview, refresh: refreshPreview } = useAdminFormPreview(formReferenceID, versionReferenceID)
  const { isReadOnly } = useAccessMode()

  // A save changes the stored graph AND the factory-built preview, so refresh both.
  const handleSaved = () => {
    onChanged()
    refreshPreview()
  }

  // Map of section reference -> factory-built preview fields, for typed (component) sections.
  const previewFieldsBySection = useMemo<Record<string, ReviewEditFieldModel[]>>(() => {
    const entries = preview?.sections ?? []
    return entries.reduce<Record<string, ReviewEditFieldModel[]>>((acc, section) => {
      acc[section.sectionReferenceID] = section.fields
      return acc
    }, {})
  }, [preview])

  const cards = [...reviewCards].sort((a, b) => a.card.order - b.card.order)
  const nextCardOrder = cards.reduce((max, item) => Math.max(max, item.card.order + 1), 1)

  const [isCardDrawerOpen, setIsCardDrawerOpen] = useState(false)
  const [editingCard, setEditingCard] = useState<AdminReviewCard | null>(null)

  const [isSectionDrawerOpen, setIsSectionDrawerOpen] = useState(false)
  const [activeCardRef, setActiveCardRef] = useState<string | null>(null)
  const [editingSection, setEditingSection] = useState<AdminReviewCardSection | null>(null)

  const [isFieldDrawerOpen, setIsFieldDrawerOpen] = useState(false)
  const [activeSectionRef, setActiveSectionRef] = useState<string | null>(null)
  const [editingField, setEditingField] = useState<AdminFormField | null>(null)
  const [editingFieldOptions, setEditingFieldOptions] = useState<AdminFormField[]>([])
  const [editingConditionSources, setEditingConditionSources] = useState<ConditionSource[]>([])
  const [editingConditions, setEditingConditions] = useState<{ dependencyRef: string; optionRef: string }[]>([])
  /** When set, the field drawer is authoring a child of a collection (stores under its key, links to Group). */
  const [collectionChildContext, setCollectionChildContext] = useState<{
    jsonObjectName: string
    groupReferenceID: string
  } | null>(null)

  const [isCollectionDrawerOpen, setIsCollectionDrawerOpen] = useState(false)
  const [editingCollection, setEditingCollection] = useState<AdminFormField | null>(null)
  const [editingCollectionConditions, setEditingCollectionConditions] = useState<
    { dependencyRef: string; optionRef: string }[]
  >([])

  const { deleteField, deleteSection, deleteCard } = useBuilderDeletes()

  // A single confirm-before-delete dialog, reused for cards, sections and fields.
  const [confirmDelete, setConfirmDelete] = useState<{
    title: string
    message: string
    run: () => Promise<void>
  } | null>(null)
  const [deleting, setDeleting] = useState(false)

  const runConfirmedDelete = async () => {
    if (!confirmDelete) return
    setDeleting(true)
    try {
      await confirmDelete.run()
      setConfirmDelete(null)
      handleSaved()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove this item.')
    } finally {
      setDeleting(false)
    }
  }

  const requestDeleteCard = (card: AdminReviewCard) =>
    setConfirmDelete({
      title: 'Delete review card',
      message: `Delete "${card.displayName || 'Untitled card'}"? This can't be undone.`,
      run: () => deleteCard(formReferenceID, versionReferenceID, card.referenceID),
    })

  const requestDeleteSection = (cardRef: string, section: AdminReviewCardSection) =>
    setConfirmDelete({
      title: 'Delete section',
      message: `Delete "${section.displayName || 'this section'}"? This can't be undone.`,
      run: () => deleteSection(cardRef, section.referenceID),
    })

  const requestDeleteField = (sectionRef: string, field: AdminFormField) =>
    setConfirmDelete({
      title: 'Delete field',
      message: `Delete "${field.editDisplayName || field.jsonPropertyName || 'this field'}"? This can't be undone.`,
      run: () => deleteField(sectionRef, field.referenceID),
    })

  const openCreateCard = () => {
    setEditingCard(null)
    setIsCardDrawerOpen(true)
  }
  const openEditCard = (card: AdminReviewCard) => {
    setEditingCard(card)
    setIsCardDrawerOpen(true)
  }
  const openAddSection = (cardRef: string) => {
    setActiveCardRef(cardRef)
    setEditingSection(null)
    setIsSectionDrawerOpen(true)
  }
  const openEditSection = (cardRef: string, section: AdminReviewCardSection) => {
    setActiveCardRef(cardRef)
    setEditingSection(section)
    setIsSectionDrawerOpen(true)
  }
  const openAddField = (sectionRef: string, sources: ConditionSource[]) => {
    setActiveSectionRef(sectionRef)
    setEditingField(null)
    setEditingFieldOptions([])
    setEditingConditionSources(sources)
    setEditingConditions([])
    setCollectionChildContext(null)
    setIsFieldDrawerOpen(true)
  }
  const openEditField = (sectionRef: string, authored: AuthoredField, sources: ConditionSource[]) => {
    setActiveSectionRef(sectionRef)
    setEditingField(authored.field)
    setEditingFieldOptions(authored.options)
    setEditingConditionSources(sources.filter((source) => source.fieldReferenceID !== authored.field.referenceID))
    setEditingConditions(authored.conditions.map(({ dependencyRef, optionRef }) => ({ dependencyRef, optionRef })))
    setCollectionChildContext(null)
    setIsFieldDrawerOpen(true)
  }

  // Collections: the drawer provisions a Collection + Group; children are authored via the field drawer in
  // "collection context" (they store under the collection's key and link to its Group).
  const openAddCollection = (sectionRef: string, sources: ConditionSource[]) => {
    setActiveSectionRef(sectionRef)
    setEditingCollection(null)
    setEditingCollectionConditions([])
    setEditingConditionSources(sources)
    setIsCollectionDrawerOpen(true)
  }
  const openEditCollection = (sectionRef: string, authored: AuthoredField, sources: ConditionSource[]) => {
    setActiveSectionRef(sectionRef)
    setEditingCollection(authored.field)
    setEditingCollectionConditions(
      authored.conditions.map(({ dependencyRef, optionRef }) => ({ dependencyRef, optionRef })),
    )
    setEditingConditionSources(sources.filter((source) => source.fieldReferenceID !== authored.field.referenceID))
    setIsCollectionDrawerOpen(true)
  }
  const openAddCollectionChild = (
    sectionRef: string,
    context: { jsonObjectName: string; groupReferenceID: string },
  ) => {
    setActiveSectionRef(sectionRef)
    setEditingField(null)
    setEditingFieldOptions([])
    setEditingConditionSources([])
    setEditingConditions([])
    setCollectionChildContext(context)
    setIsFieldDrawerOpen(true)
  }
  const openEditCollectionChild = (
    sectionRef: string,
    child: AdminFormField,
    context: { jsonObjectName: string; groupReferenceID: string },
  ) => {
    setActiveSectionRef(sectionRef)
    setEditingField(child)
    setEditingFieldOptions([])
    setEditingConditionSources([])
    setEditingConditions([])
    setCollectionChildContext(context)
    setIsFieldDrawerOpen(true)
  }

  const scrollToCard = (referenceID: string) => {
    document.getElementById(`review-card-${referenceID}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const scrollToSection = (referenceID: string) => {
    document.getElementById(`review-section-${referenceID}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const activeCard = cards.find((item) => item.card.referenceID === activeCardRef)
  const activeCardSections = activeCard ? activeCard.sections.map((entry) => entry.section) : []
  const nextSectionOrder = activeCardSections.reduce((max, section) => Math.max(max, section.order + 1), 1)

  const activeSection = cards
    .flatMap((item) => item.sections)
    .find((entry) => entry.section.referenceID === activeSectionRef)
  const nextFieldOrder = activeSection
    ? activeSection.fields.reduce((max, entry) => Math.max(max, entry.field.editOrder + 1), 1)
    : 1
  // Item keys already claimed by a Collection in the active section. A normal (non-collection-child) field
  // must not reuse one of these as its Group Key: the collection stores that key as a JSON array while a
  // plain field would try to write a property into it, which collides and previously crashed the save.
  const activeSectionCollectionItemKeys = (activeSection?.fields ?? [])
    .filter((entry) => entry.field.editType === 'Collection')
    .map((entry) => entry.field.jsonObjectName)
    .filter((name): name is string => Boolean(name))
  // The reciprocal case: Group Keys already claimed by an existing plain (object-backed) field in the active
  // section. A NEW Collection must not reuse one of these as its Item Key either - the same array/object
  // save collision, just created in the opposite order (field first, collection second).
  const activeSectionGroupKeys = (activeSection?.fields ?? [])
    .filter((entry) => entry.field.editType !== 'Collection' && !entry.field.jsonObjectIsArray)
    .map((entry) => entry.field.jsonObjectName)
    .filter((name): name is string => Boolean(name))

  return (
    <BuilderSplitLayout>
      <LeftColumn>
        <CardTile>
          <SideCardHelp>
            Build your form here — add review cards, then the sections and fields inside each. The canvas on the right
            previews what the agent sees; use its Edit links to change anything.
          </SideCardHelp>
          {!isReadOnly && (
            <OutlineAddCardLink variant="tertiary" useLinkStyle type="button" onClick={openCreateCard}>
              + Add Review Card
            </OutlineAddCardLink>
          )}
          {cards.length > 0 && (
            <OutlineTree>
              {cards.map((item) => {
                const sectionEntries = [...item.sections].sort((a, b) => a.section.order - b.section.order)
                return (
                  <OutlineCard key={item.card.referenceID}>
                    <OutlineCardName
                      variant="tertiary"
                      useLinkStyle
                      type="button"
                      onClick={() => scrollToCard(item.card.referenceID)}
                    >
                      {item.card.displayName || 'Untitled card'}
                    </OutlineCardName>
                    {sectionEntries.map((entry, index) => {
                      const section = entry.section
                      const { authored, sources } = analyseSection(entry.fields)
                      const typed = isTypedSection(section)
                      const collections = authored.filter((candidate) => candidate.collection)
                      return (
                        <OutlineSection key={section.referenceID}>
                          <OutlineSectionName
                            variant="tertiary"
                            useLinkStyle
                            type="button"
                            onClick={() => scrollToSection(section.referenceID)}
                          >
                            {section.displayName || `Section ${index + 1}`}
                          </OutlineSectionName>
                          {typed ? (
                            <OutlineSectionNote>Component-generated</OutlineSectionNote>
                          ) : (
                            <>
                              {!isReadOnly && (
                                <OutlineActions>
                                  <OutlineAddLink
                                    variant="tertiary"
                                    useLinkStyle
                                    type="button"
                                    onClick={() => openAddField(section.referenceID, sources)}
                                  >
                                    + Add Field
                                  </OutlineAddLink>
                                  <OutlineAddLink
                                    variant="tertiary"
                                    useLinkStyle
                                    type="button"
                                    onClick={() => openAddCollection(section.referenceID, sources)}
                                  >
                                    + Add Collection
                                  </OutlineAddLink>
                                </OutlineActions>
                              )}
                              {collections.map((entryCollection) => (
                                <OutlineCollection key={entryCollection.field.referenceID}>
                                  <OutlineSectionName
                                    variant="tertiary"
                                    useLinkStyle
                                    type="button"
                                    onClick={() => scrollToSection(section.referenceID)}
                                  >
                                    {entryCollection.field.editDisplayName ||
                                      entryCollection.field.jsonObjectName ||
                                      'Collection'}{' '}
                                    (Collection)
                                  </OutlineSectionName>
                                  {!isReadOnly && (
                                    <OutlineActions>
                                      <OutlineAddLink
                                        variant="tertiary"
                                        useLinkStyle
                                        type="button"
                                        onClick={() =>
                                          entryCollection.collection?.groupReferenceID &&
                                          openAddCollectionChild(section.referenceID, {
                                            jsonObjectName: entryCollection.field.jsonObjectName ?? '',
                                            groupReferenceID: entryCollection.collection.groupReferenceID,
                                          })
                                        }
                                      >
                                        + Add Field
                                      </OutlineAddLink>
                                    </OutlineActions>
                                  )}
                                </OutlineCollection>
                              ))}
                            </>
                          )}
                        </OutlineSection>
                      )
                    })}
                    {!isReadOnly && (
                      <OutlineActions>
                        <OutlineAddLink
                          variant="tertiary"
                          useLinkStyle
                          type="button"
                          onClick={() => openAddSection(item.card.referenceID)}
                        >
                          + Add Section
                        </OutlineAddLink>
                      </OutlineActions>
                    )}
                  </OutlineCard>
                )
              })}
            </OutlineTree>
          )}
        </CardTile>
      </LeftColumn>
      <Canvas>
        {cards.length === 0 ? (
          <EmptyState>No review cards yet. Add a review card to start building the agent view.</EmptyState>
        ) : (
          cards.map((item, index) => {
            const sectionEntries = [...item.sections].sort((a, b) => a.section.order - b.section.order)
            return (
              <div key={item.card.referenceID} id={`review-card-${item.card.referenceID}`}>
                {index > 0 && <CardDivider />}
                <SectionTile title={item.card.displayName || 'Untitled card'} iconName={item.card.icon || undefined}>
                  {item.card.description && <CanvasCardDescription>{item.card.description}</CanvasCardDescription>}
                  {sectionEntries.length === 0 ? (
                    <NoSectionsNote>No sections yet.</NoSectionsNote>
                  ) : (
                    <SectionsList>
                      {sectionEntries.map((entry) => {
                        const section = entry.section
                        const { authored: authoredFields, sources: conditionSources } = analyseSection(entry.fields)
                        const typed = isTypedSection(section)
                        // Flags a typed section whose type doesn't apply to this family — e.g. a tenant-signature
                        // section left over on a Notice from before the family was finalised, or hand-authored
                        // against the API directly. Informational only; see notice-section-types.ts.
                        const isFamilyMismatch =
                          family === 'Notice' && typed && !NOTICE_SECTION_TYPES.has(section.sectionType)
                        // A section can only be removed once it holds no fields (mirrors the backend's block rule).
                        const sectionRemovable = entry.fields.length === 0
                        return (
                          <SectionBlock key={section.referenceID} id={`review-section-${section.referenceID}`}>
                            {section.displayName && (
                              <SectionBlockHeader>
                                <SectionBlockTitle>{section.displayName}</SectionBlockTitle>
                              </SectionBlockHeader>
                            )}
                            {section.displayDescription && (
                              <SectionBlockDescription>{section.displayDescription}</SectionBlockDescription>
                            )}
                            {typed ? (
                              previewFieldsBySection[section.referenceID]?.length ? (
                                <>
                                  <FactorySectionCaption>
                                    Generated by the {getEnumLabel(enums, 'SectionType', section.sectionType)} component
                                    · not individually editable
                                  </FactorySectionCaption>
                                  <FactorySectionPreview fields={previewFieldsBySection[section.referenceID]} />
                                </>
                              ) : (
                                <SectionTypeNote>
                                  {getEnumLabel(enums, 'SectionType', section.sectionType)} · fields generated
                                  automatically by this component
                                </SectionTypeNote>
                              )
                            ) : (
                              authoredFields.length > 0 && (
                                <FieldPreviewGrid>
                                  {authoredFields.map((authored) => {
                                    return authored.collection ? (
                                      <CollectionPreview
                                        key={authored.field.referenceID}
                                        collection={authored.field}
                                        childFields={authored.collection.children}
                                        conditions={authored.conditions}
                                        onEditChild={(child) =>
                                          authored.collection?.groupReferenceID &&
                                          openEditCollectionChild(section.referenceID, child, {
                                            jsonObjectName: authored.field.jsonObjectName ?? '',
                                            groupReferenceID: authored.collection.groupReferenceID,
                                          })
                                        }
                                        onEditCollection={() =>
                                          openEditCollection(section.referenceID, authored, conditionSources)
                                        }
                                        onRemoveChild={(child) => requestDeleteField(section.referenceID, child)}
                                        onRemove={() => requestDeleteField(section.referenceID, authored.field)}
                                        readOnly={isReadOnly}
                                      />
                                    ) : (
                                      <FieldPreview
                                        key={authored.field.referenceID}
                                        field={authored.field}
                                        options={authored.options}
                                        conditions={authored.conditions}
                                        onEdit={() => openEditField(section.referenceID, authored, conditionSources)}
                                        onRemove={() => requestDeleteField(section.referenceID, authored.field)}
                                        readOnly={isReadOnly}
                                      />
                                    )
                                  })}
                                </FieldPreviewGrid>
                              )
                            )}
                            {isFamilyMismatch && (
                              <SectionFamilyMismatchNote>
                                ⚠ {getEnumLabel(enums, 'SectionType', section.sectionType)} doesn&apos;t apply to
                                Notices — consider changing this section&apos;s type.
                              </SectionFamilyMismatchNote>
                            )}
                            {!isReadOnly && (
                              <SectionFooter>
                                <SectionEditRow>
                                  <Button
                                    variant="tertiary"
                                    useLinkStyle
                                    size="small"
                                    type="button"
                                    onClick={() => openEditSection(item.card.referenceID, section)}
                                  >
                                    Edit
                                  </Button>
                                  {sectionRemovable && (
                                    <Button
                                      variant="tertiary"
                                      useLinkStyle
                                      isDestructive
                                      size="small"
                                      type="button"
                                      onClick={() => requestDeleteSection(item.card.referenceID, section)}
                                    >
                                      Delete
                                    </Button>
                                  )}
                                </SectionEditRow>
                              </SectionFooter>
                            )}
                          </SectionBlock>
                        )
                      })}
                    </SectionsList>
                  )}
                  {!isReadOnly && (
                    <CardActions>
                      <SectionEditRow>
                        <Button
                          variant="tertiary"
                          useLinkStyle
                          size="small"
                          type="button"
                          onClick={() => openEditCard(item.card)}
                        >
                          Edit
                        </Button>
                        {sectionEntries.length === 0 && (
                          <Button
                            variant="tertiary"
                            useLinkStyle
                            isDestructive
                            size="small"
                            type="button"
                            onClick={() => requestDeleteCard(item.card)}
                          >
                            Delete
                          </Button>
                        )}
                      </SectionEditRow>
                    </CardActions>
                  )}
                </SectionTile>
              </div>
            )
          })
        )}
      </Canvas>

      <ReviewCardDrawer
        isOpen={isCardDrawerOpen}
        onOpenChange={setIsCardDrawerOpen}
        formReferenceID={formReferenceID}
        versionReferenceID={versionReferenceID}
        card={editingCard}
        existingCards={cards.map((item) => item.card)}
        nextOrder={nextCardOrder}
        onSaved={handleSaved}
      />
      <SectionDrawer
        isOpen={isSectionDrawerOpen}
        onOpenChange={setIsSectionDrawerOpen}
        reviewCardReferenceID={activeCardRef ?? ''}
        section={editingSection}
        existingSections={activeCardSections}
        nextOrder={nextSectionOrder}
        family={family}
        onSaved={handleSaved}
      />
      <FieldDrawer
        isOpen={isFieldDrawerOpen}
        onOpenChange={setIsFieldDrawerOpen}
        sectionReferenceID={activeSectionRef ?? ''}
        field={editingField}
        options={editingFieldOptions}
        conditionSources={editingConditionSources}
        currentConditions={editingConditions}
        collectionContext={collectionChildContext}
        reservedCollectionItemKeys={activeSectionCollectionItemKeys}
        nextOrder={nextFieldOrder}
        onSaved={handleSaved}
      />
      <CollectionDrawer
        isOpen={isCollectionDrawerOpen}
        onOpenChange={setIsCollectionDrawerOpen}
        sectionReferenceID={activeSectionRef ?? ''}
        collection={editingCollection}
        conditionSources={editingConditionSources}
        currentConditions={editingCollectionConditions}
        reservedGroupKeys={activeSectionGroupKeys}
        nextOrder={nextFieldOrder}
        onSaved={handleSaved}
      />
      <ConfirmDeleteDialog
        isOpen={Boolean(confirmDelete)}
        title={confirmDelete?.title ?? ''}
        message={confirmDelete?.message ?? ''}
        busy={deleting}
        onConfirm={runConfirmedDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </BuilderSplitLayout>
  )
}
